import { useEffect, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectAuth } from '@/features/auth/authSlice'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useNotificationCounts } from '@/hooks/useNotificationCounts'
import {
  combinedDrawerBadgeCount,
  getUserInitials,
} from '@/features/invitee/utils/userDrawerHelpers'
import { UserDrawer } from './UserDrawer'
import type { UserMenuButtonProps } from '../types/components'

/**
 * Profile trigger + notification badge + drawer (Angular layout page-header profile).
 */
export function UserMenuButton({
  testIdPrefix = 'user-menu',
  className = '',
}: UserMenuButtonProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [avatarBroken, setAvatarBroken] = useState(false)
  const { user } = useAppSelector(selectAuth)
  const { counts, todoPrompt, dismissTodoPrompt, refreshCounts } =
    useNotificationCounts(true)

  const totalBadge = combinedDrawerBadgeCount(counts)
  const initials = getUserInitials(user?.first_name, user?.last_name)
  const displayName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()

  useEffect(() => {
    setAvatarBroken(false)
  }, [user?.profile_pic])

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          type="button"
          data-testid={`${testIdPrefix}-user-menu-button`}
          aria-label={displayName ? `Open user menu for ${displayName}` : 'Open user menu'}
          className="flex max-w-full items-center gap-2 rounded-full py-0.5 pl-1 pr-0 text-left transition-colors select-none hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)] focus-visible:ring-offset-1"
          onClick={() => setIsDrawerOpen(true)}
        >
          {displayName ? (
            <span
              data-testid={`${testIdPrefix}-user-name`}
              className="max-w-[9.5rem] truncate text-sm font-semibold leading-none text-slate-900 sm:max-w-[14rem]"
            >
              {displayName}
            </span>
          ) : null}
          <span className="relative shrink-0">
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-[rgb(21_151_255)]/20 bg-teal-600 text-white shadow-sm sm:h-9 sm:w-9">
              {user?.profile_pic && !avatarBroken ? (
                <img
                  src={user.profile_pic}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setAvatarBroken(true)}
                />
              ) : (
                <span className="text-[11px] font-bold leading-none sm:text-xs" aria-hidden>
                  {initials}
                </span>
              )}
            </span>
            {totalBadge > 0 ? (
              <span
                data-testid={`${testIdPrefix}-notification-badge`}
                className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold text-white"
              >
                {totalBadge}
              </span>
            ) : null}
          </span>
        </button>
      </div>

      <UserDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onCountsRefresh={refreshCounts}
      />

      {todoPrompt?.header && todoPrompt.description ? (
        <ConfirmDialog
          isOpen
          title={todoPrompt.header}
          message={todoPrompt.description}
          primaryLabel="Ok"
          onPrimary={dismissTodoPrompt}
          secondaryLabel=""
          onSecondary={dismissTodoPrompt}
          onDismiss={dismissTodoPrompt}
        />
      ) : null}
    </>
  )
}
